import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Edit, Trash2, Network, CheckCircle, Building2, Eye } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import StatCard from '../../components/common/StatCard';
import ChannelModal, { type ChannelFormData } from '../../components/admin/ChannelModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import channelService, { CHANNEL_STATUS, type Channel as ApiChannel } from '../../services/channel.service';
import tenantService, { type Tenant } from '../../services/tenant.service';
import { useAuthStore } from '../../stores/authStore';
import { USER_TYPES } from '../../utils/constants';
import { useToast } from '../../components/common/ToastContainer';
import { apiErrorMessage } from '../../utils/apiError';

interface ChannelRecord extends ApiChannel {
  tenantCount: number;
}

/**
 * Channels — the middle layer between SuperAdmin and Tenant.
 *
 * A SuperAdmin gets full CRUD. A ChannelAdmin gets a read-only list of the
 * channels they administer, and this page is their home when no tenant is
 * selected. The write affordances are gated on user type, deliberately NOT on
 * usePermissions() — that hook returns all-true for a ChannelAdmin (they have
 * no tenant-scoped role), and a ChannelAdmin must never be able to create a
 * channel or appoint an admin, since either would widen their own scope.
 */
const Channels = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;

  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState<ChannelRecord[]>([]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<ChannelFormData | undefined>();
  const [deleteChannel, setDeleteChannel] = useState<ChannelRecord | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      // Tenant counts come from one scope-aware /tenants call rather than a
      // per-channel fan-out over /channels/:id/tenants.
      const [channelItems, tenantItems] = await Promise.all([
        channelService.listAll(),
        // Counts are decoration — a failure here must not empty the list.
        tenantService.listAll().catch(() => [] as Tenant[]),
      ]);

      const counts = new Map<string, number>();
      for (const t of tenantItems) {
        if (t.channelId) counts.set(t.channelId, (counts.get(t.channelId) ?? 0) + 1);
      }

      setChannels(channelItems.map((c) => ({ ...c, tenantCount: counts.get(c.id) ?? 0 })));
    } catch (error) {
      console.error('Failed to fetch channels:', error);
      toast.error('Load Failed', apiErrorMessage(error, 'Failed to load channels'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChannels();
    // Load once on mount; the Refresh button re-invokes fetchChannels directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveChannel = async (data: ChannelFormData) => {
    try {
      const payload = {
        name: data.name,
        slug: data.slug || undefined,
        description: data.description || undefined,
        contactEmail: data.contactEmail || undefined,
        status: data.status,
      };

      if (data.id) {
        await channelService.update(data.id, payload);
        toast.success('Channel Updated', `${data.name} updated successfully`);
      } else {
        await channelService.create(payload);
        toast.success('Channel Created', `${data.name} created successfully`);
      }

      await fetchChannels();
      setIsAddModalOpen(false);
      setEditingChannel(undefined);
    } catch (error) {
      console.error('Failed to save channel:', error);
      toast.error('Save Failed', apiErrorMessage(error, 'Failed to save channel. Please try again.'));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteChannel) return;
    try {
      setIsDeleting(true);
      await channelService.remove(deleteChannel.id);
      toast.success('Channel Deleted', `${deleteChannel.name} has been deleted`);
      await fetchChannels();
      setDeleteChannel(undefined);
    } catch (error) {
      // The server refuses to delete a channel that still owns tenants —
      // surface its message rather than a generic failure.
      console.error('Failed to delete channel:', error);
      toast.error('Delete Failed', apiErrorMessage(error, 'Failed to delete channel.'));
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: Column<ChannelRecord>[] = useMemo(() => {
    const cols: Column<ChannelRecord>[] = [
      {
        key: 'name',
        label: 'Channel',
        sortable: true,
        render: (c) => (
          <div className="flex items-center space-x-2">
            <Network className="w-4 h-4 text-primary-600" />
            <button
              onClick={() => navigate(`/admin/channels/${c.id}`)}
              className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
            >
              {c.name}
            </button>
          </div>
        ),
      },
      { key: 'slug', label: 'Slug', sortable: true, render: (c) => <span className="text-gray-600">{c.slug || '-'}</span> },
      {
        key: 'contactEmail',
        label: 'Contact',
        sortable: true,
        render: (c) => <span className="text-gray-600">{c.contactEmail || '-'}</span>,
      },
      {
        key: 'tenantCount',
        label: 'Tenants',
        sortable: true,
        render: (c) => (
          <div className="flex items-center space-x-1">
            <Building2 className="w-3 h-3 text-gray-400" />
            <span>{c.tenantCount}</span>
          </div>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        render: (c) => (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              c.status === CHANNEL_STATUS.ACTIVE
                ? 'bg-success-100 text-success-700'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {c.status === CHANNEL_STATUS.ACTIVE ? 'Active' : 'Suspended'}
          </span>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (c) => (
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => navigate(`/admin/channels/${c.id}`)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="View Details"
            >
              <Eye className="w-4 h-4 text-gray-600 hover:text-primary-600" />
            </button>
            {isSuperAdmin && (
              <button
                onClick={() =>
                  setEditingChannel({
                    id: c.id,
                    name: c.name,
                    slug: c.slug || '',
                    description: c.description || '',
                    contactEmail: c.contactEmail || '',
                    status: c.status,
                  })
                }
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                title="Edit Channel"
              >
                <Edit className="w-4 h-4 text-gray-600 hover:text-primary-600" />
              </button>
            )}
            {isSuperAdmin && (
              <button
                onClick={() => setDeleteChannel(c)}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                title="Delete Channel"
              >
                <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
              </button>
            )}
          </div>
        ),
      },
    ];
    return cols;
  }, [isSuperAdmin, navigate]);

  const activeCount = channels.filter((c) => c.status === CHANNEL_STATUS.ACTIVE).length;
  const totalTenants = channels.reduce((sum, c) => sum + c.tenantCount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Channels</h1>
          <p className="text-gray-600 mt-1">
            {isSuperAdmin
              ? 'Manage channel partners and the tenants they own'
              : 'Channels you administer. Open one to work with its tenants.'}
          </p>
        </div>
        <div className="flex space-x-3">
          <button onClick={fetchChannels} className="btn-secondary inline-flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add Channel</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Network} title="Channels" value={channels.length} color="primary" />
        <StatCard icon={CheckCircle} title="Active" value={activeCount} color="success" />
        <StatCard icon={Building2} title="Tenants" value={totalTenants} color="info" />
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={channels}
          searchPlaceholder="Search channels..."
          loading={loading}
        />
      </div>

      {isSuperAdmin && (
        <>
          {/* `key` remounts the form so it re-seeds from props — see ChannelModal. */}
          <ChannelModal
            key={isAddModalOpen ? 'add-open' : 'add-closed'}
            isOpen={isAddModalOpen}
            onClose={() => setIsAddModalOpen(false)}
            onSave={handleSaveChannel}
            mode="add"
          />

          <ChannelModal
            key={editingChannel?.id ?? 'edit-closed'}
            isOpen={!!editingChannel}
            onClose={() => setEditingChannel(undefined)}
            onSave={handleSaveChannel}
            channel={editingChannel}
            mode="edit"
          />

          <ConfirmDialog
            isOpen={!!deleteChannel}
            onClose={() => setDeleteChannel(undefined)}
            onConfirm={handleDeleteConfirm}
            title="Delete Channel"
            message={
              deleteChannel?.tenantCount
                ? `"${deleteChannel.name}" still owns ${deleteChannel.tenantCount} tenant(s). Move or delete them first — the server will refuse this deletion.`
                : `Are you sure you want to delete channel "${deleteChannel?.name}"?\n\nThis action cannot be undone.`
            }
            confirmText="Delete"
            cancelText="Cancel"
            variant="danger"
            isLoading={isDeleting}
          />
        </>
      )}
    </div>
  );
};

export default Channels;
